import { animaliaListingDesignTokens } from '../shared/animalia-listing-design-tokens';

interface AnimaliaListingStatusAction {
  readonly href: string;
  readonly label: string;
}

interface AnimaliaListingStatusOptions {
  readonly showRetry?: boolean;
  readonly secondaryAction?: AnimaliaListingStatusAction | null;
  readonly kicker?: string;
}

export class MvAnimaliaListingStatus extends HTMLElement {
  private variant: 'loading' | 'empty' | 'error' = 'loading';

  private heading = '';

  private message = '';

  private showRetry = false;

  private secondaryAction: AnimaliaListingStatusAction | null = null;

  private kicker = 'Estado del listado';

  private readonly handleRetryClick = () => {
    this.dispatchEvent(
      new CustomEvent('retry-requested', {
        bubbles: true,
        composed: true,
      }),
    );
  };

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  setStatus(
    variant: 'loading' | 'empty' | 'error',
    title: string,
    body: string,
    options: AnimaliaListingStatusOptions = {},
  ): void {
    this.variant = variant;
    this.heading = title;
    this.message = body;
    this.showRetry = options.showRetry ?? false;
    this.secondaryAction = options.secondaryAction ?? null;
    this.kicker = options.kicker ?? 'Estado del listado';
    this.render();
  }

  connectedCallback(): void {
    this.render();
    this.addEventListeners();
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }

  private render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const retryButton = this.showRetry
      ? '<button class="action-button" type="button" data-retry>Reintentar</button>'
      : '';
    const secondaryActionButton = this.secondaryAction
      ? `<a class="action-button secondary-action" href="${this.escapeHtml(this.secondaryAction.href)}">${this.escapeHtml(this.secondaryAction.label)}</a>`
      : '';

    this.shadowRoot.innerHTML = `
      <style>
        ${animaliaListingDesignTokens}

        article {
          padding: 22px;
        }

        h2 {
          margin: 8px 0 0;
          color: #31463d;
          font-size: clamp(1.5rem, 2.4vw, 2.1rem);
          font-family: Georgia, 'Times New Roman', serif;
        }

        p:last-of-type {
          margin: 10px 0 0;
          line-height: 1.7;
          color: rgba(32, 39, 32, 0.64);
        }

        .action-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-top: 18px;
          padding: 12px 16px;
          border-radius: 999px;
          border: 1px solid #31463d;
          background: #31463d;
          color: #f7f3ed;
          cursor: pointer;
          text-decoration: none;
        }

        .secondary-action {
          margin-left: 12px;
          background: transparent;
          color: #31463d;
        }
      </style>

      <article class="editorial-panel" data-variant="${this.variant}">
        <p class="status-kicker">${this.escapeHtml(this.kicker)}</p>
        <h2>${this.escapeHtml(this.heading)}</h2>
        <p>${this.escapeHtml(this.message)}</p>
        ${retryButton}
        ${secondaryActionButton}
      </article>
    `;

    this.addEventListeners();
  }

  private addEventListeners(): void {
    this.shadowRoot?.querySelector('[data-retry]')?.addEventListener('click', this.handleRetryClick);
  }

  private removeEventListeners(): void {
    this.shadowRoot?.querySelector('[data-retry]')?.removeEventListener('click', this.handleRetryClick);
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('mv-animalia-listing-status')) {
  customElements.define('mv-animalia-listing-status', MvAnimaliaListingStatus);
}
